import React from "react";
import { Text } from "@chakra-ui/react";
import { Button, IconButton, IconButtonProps } from "@chakra-ui/button";
import { Box, BoxProps, Divider, HStack, VStack } from "@chakra-ui/layout";
import { DeleteIcon } from "@chakra-ui/icons";
import { PlusIcon } from "../../../styles/icons";

interface AddRemoveBoxFieldProps<T> {
  headerLabel: string;
  fieldsName: string;
  fields: T[] | null | undefined;
  newField: T;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  children: (
    fieldsName: string,
    fields: T[] | null | undefined,
    field: T,
    fieldIdx: number,
    DeleteButton: ({
      idx,
      iconProps,
      ...boxProps
    }: {
      idx: number;
      iconProps?: IconButtonProps;
    } & BoxProps) => JSX.Element
  ) => JSX.Element;
}

export function AddRemoveBoxField<T>({
  headerLabel,
  fieldsName,
  fields,
  newField,
  setFieldValue,
  children,
  ...props
}: AddRemoveBoxFieldProps<T> & BoxProps) {
  const deleteButton = ({
    idx,
    iconProps,
    ...boxProps
  }: { idx: number; iconProps?: IconButtonProps } & BoxProps) => (
    <Box h="100%" pb={1} alignSelf="flex-end" {...boxProps}>
      <IconButton
        title={`Delete ${headerLabel}.`}
        icon={<DeleteIcon />}
        size="sm"
        onClick={() => {
          fields?.splice(idx, 1);
          setFieldValue(fieldsName, fields);
        }}
        {...iconProps}
        aria-label={`Delete ${headerLabel}.`}
      />
    </Box>
  );

  return (
    <Box
      w="100%"
      p={4}
      border="1px solid"
      borderColor="cherryButtonDark.100"
      borderRadius="md"
      {...props}
    >
      <HStack w="100%">
        <Text mr="auto" fontSize="sm">
          <b>{headerLabel}</b>
        </Text>
        <Button
          ml="auto"
          mr="0"
          size="xs"
          leftIcon={<PlusIcon />}
          onClick={() => {
            if (fields == null) {
              setFieldValue(fieldsName, [newField]);
            } else {
              fields?.push(newField);
              setFieldValue(fieldsName, fields);
            }
          }}
          colorScheme="cherryButton"
        >
          Add {headerLabel}
        </Button>
      </HStack>
      <Divider mb={2} mt={1} />
      <VStack w="100%">
        {(fields?.length &&
          fields?.map((typeGroup: any, typeGroupIdx: number) => {
            return children(
              fieldsName,
              fields,
              typeGroup,
              typeGroupIdx,
              deleteButton
            );
          })) || (
          <Text fontStyle="italic" fontSize="sm">
            {headerLabel} has not been set.
          </Text>
        )}
      </VStack>
    </Box>
  );
}
